import axios from 'axios'
import CommonUtils from '@bodeboca/utils'
import {bool} from "prop-types"

export default class ApiService {

  private static availableBundlesToFlag: string[] = []

  public static getDomain(): string {
    if (drupalSettings['algolia'].domain !== undefined) {
      return drupalSettings['algolia'].domain
    }

    return 'www_bodeboca_com'
  }

  public static getSearchFilters(): any[] {
    let contextFilters = []

    const filters = drupalSettings['algolia'].config.search_filters
    for (let i = 0; i < filters.length; i++) {
      if (CommonUtils.containsAny(filters[i]['context'], drupalSettings['algolia']['context'])) {
        contextFilters.push(filters[i])
      }
    }

    return contextFilters
  }

  public static getAutocompleteSections(): any[] {
    const sections = drupalSettings['algolia'].config.autocomplete_filters

    for (let i = 0; i < sections.length; i++) {
      sections[i].query_url = CommonUtils.replacePlaceholders(sections[i].query_url)
      sections[i].key = CommonUtils.replacePlaceholders(sections[i].key)
    }
    return sections
  }

  public static async addToCart(id: any, qty: number, title: string, queryId?: string) {

    const csrfToken = await ApiService.getCsrfToken()
    let url = ApiService.getPrefixedUrl('api/v4/cart')
    const data = {
      nid: id,
      qty,
      product_title: title,
      queryId,
      cart_page: location.pathname === '/cart' ? 1 : undefined,
    }
    CommonUtils.setIsLoading(true)
    axios
      .post(url, data, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
      })
      .then((data) => {
        CommonUtils.updateCartBlock(data.data.cart_block)

        if (data.data.notices !== undefined) {
          CommonUtils.showBbAjaxMessage(data.data.notices[0], 'error')
        } else {
          CommonUtils.showBbAjaxMessage(Drupal.t('@product has been successfully added to your cart.', {
            '@product': title,
          }))
        }
        CommonUtils.setIsLoading(false)
      })
      .catch((err) => {
        CommonUtils.showBbAjaxMessage(Drupal.t('Ooops! Something went wrong.'), 'error')
        CommonUtils.setIsLoading(false)
      })
  }

  public static getUserId(): number {
    if (drupalSettings['user'] !== undefined) {
      return drupalSettings['user'].uid
    }
    return 0
  }

  public static getAppId(): string {
    return drupalSettings['algolia'].app_id
  }

  public static getApiKey(): string {
    return drupalSettings['algolia'].api_key
  }

  public static getContextFilters(): string {

    return CommonUtils.replacePlaceholders(drupalSettings['algolia'].filters)
  }

  public static isBlockActive(block: string): boolean {
    return drupalSettings['algolia'].blocks[block]
  }

  public static getFlechazos(): [] {
    return drupalSettings['filters']['flechazo_user']
  }

  public static getItemFromId(attribute: string, label: string): any | undefined {

    // Here we check if is a language contextual field.
    let decomposed = attribute.split('.')
    if (decomposed.length > 1 && decomposed[0].length === 2) {
      decomposed.shift()
      attribute = decomposed.join('.')
    }

    if (drupalSettings['filters'] !== undefined) {
      if (drupalSettings['filters'][attribute] !== undefined) {
        // Here we have defined the facet to be translated.
        let search = drupalSettings['filters'][attribute].find((el) => {
          return el.id == parseInt(label)
        })
        if (search) {
          search.hide = false
          return search
        } else {
          // If we do not find tanslation (field not exposed) we should hide it.
          return {
            label: label, hide: true
          }
        }
      }
    }
    // If we have not defined facet to be translated, always show.
    return {
      label: label, hide: false
    }
  }

  public static translateSlugToId(attribute: string, slug: string) {
    let decomposed = attribute.split('.')
    if (decomposed.length > 1 && decomposed[0].length === 2) {
      decomposed.shift()
      attribute = decomposed.join('.')
    }
    if (drupalSettings['filters'] !== undefined) {
      if (drupalSettings['filters'][attribute] !== undefined) {
        let search = drupalSettings['filters'][attribute].find((el) => {
          return el.slug == slug
        })
        if (search) {
          return parseInt(search.id)
        }
      }
    }

    return slug
  }

  public static translateIdToSlug(attribute: string, id: string): string {
    if (drupalSettings['filters'] !== undefined) {
      if (drupalSettings['filters'][attribute] !== undefined) {
        let search = drupalSettings['filters'][attribute].find((el) => {
          return el.id == id
        })
        if (search) {
          return search.slug
        }
      }
    }

    return id
  }

  public static async getFavouritesArray(): Promise<any> {
    if (ApiService.favouritesArrayPromise) {
      return ApiService.favouritesArrayPromise
    }
    ApiService.favouritesArrayPromise = (async () => {
      const csrfToken = await ApiService.getCsrfToken()
      if (ApiService.getUserId() == 0) {
        ApiService.setFavourites([])
        ApiService.favouritesArrayPromise = null
        return []
      }
      const currentFavourites = ApiService.getFavourites()
      if (currentFavourites.length > 0) {
        ApiService.favouritesArrayPromise = null
        return currentFavourites
      }
      const url = ApiService.getPrefixedUrl('api/v4/user/favourite/' + this.getUserId())
      try {
        const data = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })
        if (data.data.favourites) {
          ApiService.setFavourites(data.data.favourites)
          return Object.keys(data.data.favourites)
        }
        return []
      } catch {
        ApiService.setFavourites([])
        return []
      } finally {
        ApiService.favouritesArrayPromise = null
      }
    })()
    return ApiService.favouritesArrayPromise
  }

  public static async getEmptySearchMenu(): Promise<any> {
    return new Promise((resolve, reject) => {

      let url = ApiService.getPrefixedUrl('api/bb-algolia-empty-search-menu')
      axios
        .get(url)
        .then((data) => {
          if (data.data.html) {
            resolve(data.data.html)
          }

        }).catch(() => {
          resolve('')
        })

    })
  }

  public static getSortOptions(): any {
    let returnOptions = []
    let options = drupalSettings['algolia'].config.sort_options

    for (let i = 0; i < options.length; i++) {

      if (CommonUtils.containsAny(options[i]['context'], ApiService.getContext())) {

        returnOptions.push(options[i])
      }
    }
    return returnOptions
  }

  public static getDefaultIndex(): string {
    let defaultSort = ApiService.getDefaultSort()
    if (defaultSort !== undefined) {
      return defaultSort.value
    }
    return drupalSettings['algolia']['default_index']
  }

  public static getDefaultAutocompleteIndex(): string {
    return drupalSettings['algolia']['default_index']
  }

  public static getDefaultSort(): any {
    const options = ApiService.getSortOptions()
    let defaultSort = ''
    if (drupalSettings['algolia']['default_sort_index'] !== undefined) {
      defaultSort = drupalSettings['algolia']['default_sort_index']
    }

    for (let i = 0; i < options.length; i++) {
      if (options[i]['value'] === defaultSort) {
        return options[i]
      }
    }

    return options[0]
  }

  public static isFavourite(id: string) {
    if (drupalSettings['user'].favourites !== undefined && drupalSettings['user'].favourites !== null) {
      return drupalSettings['user'].favourites.includes(id + '')
    }

    return false
  }

  public static getFavourites(): [] {
    if (ApiService.getUserId() == 0) {
      return []
    }

    return Object.keys(drupalSettings['user'].favourites)
  }

  public static async toggleFavorite(id: string, operation: any): Promise<any> {
    const self = this
    const csrfToken = await ApiService.getCsrfToken()
    return new Promise((resolve, reject) => {
      let url = ApiService.getPrefixedUrl('api/v4/user/favourite/' + id)
      if (operation === 'add') {
        axios
          .patch(url, {}, {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            }
          })
          .then((data) => {
            ApiService.setFavourites(Object.keys(data.data.favourites))
            resolve(Object.keys(data.data.favourites))
          })
          .catch((err) => {
            reject()
          })
      } else {
        axios
          .delete(url, {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            }
          })
          .then((data) => {
            ApiService.setFavourites(Object.keys(data.data.favourites))
            resolve(true)
          })
          .catch((err) => {
            reject()
          })
      }
    })
  }

  public static getContext(): [] {
    if (drupalSettings['algolia']['context'] !== undefined) {
      return drupalSettings['algolia']['context']
    }
    return []
  }

  public static getRoutingContext(): string {
    return drupalSettings['algolia']['routing_context']
  }

  public static isContext(context: string): boolean {

    return ApiService.getContext().indexOf(context) !== -1
  }

  public static getHeader(): [] | undefined {

    if (drupalSettings['algolia']['header']) {
      return drupalSettings['algolia']['header']
    }

    return undefined
  }

  public static getRestrictSearchableAttributes(): [] {

    let searchableAttributes = drupalSettings['algolia']['config']['restrict_searchable_attributes']
    if (searchableAttributes === undefined) {
      return []
    }
    for (let i = 0; i < searchableAttributes.length; i++) {
      searchableAttributes[i] = CommonUtils.replacePlaceholders(searchableAttributes[i])
    }

    return searchableAttributes
  }

  public static getBannerInfo(): object | null {
    if (drupalSettings['algolia']['banner_info']) {
      return drupalSettings['algolia']['banner_info']
    }

    return
  }

  public static getBanners(): [] {

    let bannerInfo = ApiService.getBannerInfo()
    if (bannerInfo !== null && bannerInfo !== undefined) {
      if (bannerInfo['banners'] !== undefined) {
        return bannerInfo['banners']
      }
    }

    return []
  }

  public static getPageOffset(): number {
    let bannerInfo = ApiService.getBannerInfo()

    if (bannerInfo !== null && bannerInfo !== undefined) {
      return bannerInfo['offset']
    }
    return 0
  }

  public static error(message) {
    throw new Error(message)
  }

  public static setUserFlaggedStockItems(flaggedItems: any) {

    drupalSettings['user'].flaggedItems = flaggedItems
  }

  public static getUserFlaggedStockItems() {

    if (ApiService.getUserId() == 0) {
      return []
    }
    return drupalSettings['user'].flaggedItems
  }

  public static async getFlaggedStockItems(): Promise<any> {

    return new Promise((resolve, reject) => {

      const userId = ApiService.getUserId()
      if (userId !== 0) {
        let url = ApiService.getPrefixedUrl('api/v4/flags/stock')
        axios
          .get(url)
          .then((response) => {
            if (Object.keys(response.data).length !== 0) {
              ApiService.setUserFlaggedStockItems(response.data.items)
              resolve(response.data)
            }
          })
          .catch(() => {
            ApiService.setUserFlaggedStockItems([])
            resolve([])
          })
      } else {
        ApiService.setUserFlaggedStockItems([])
        resolve([])
      }
    })
  }

  public static getCsrfToken = async () => {
    if (drupalSettings.csrf_token) {
      return drupalSettings.csrf_token
    }
    if (!ApiService.csrfTokenPromise) {
      ApiService.csrfTokenPromise = (async () => {
        try {
          const url = ApiService.getPrefixedUrl('session/token')
          const response = await axios.get(url)
          drupalSettings.csrf_token = response.data
          return response.data
        } catch (error) {
          ApiService.csrfTokenPromise = null
          throw error
        }
      })()
    }
    return ApiService.csrfTokenPromise
  }

  public static isRoutingEnabled(): boolean {
    return Boolean(drupalSettings['algolia']['routing_enabled'])
  }

  public static getPathPrefix(): string {

    if (drupalSettings.path.pathPrefix !== undefined) {
      return drupalSettings.path.pathPrefix
    }

    return ''
  }

  public static getPrefixedUrl(url: string): string {

    const pathPrefix = ApiService.getPathPrefix()

    url = '/' + pathPrefix + url

    return url
  }

  public static getRankingObject(): any {
    return drupalSettings.algolia.ranking ?? undefined
  }

  public static isMainSection(): bool {
    if (drupalSettings['algolia']['is_main_section'] === undefined) {
      return false
    }
    return drupalSettings['algolia']['is_main_section']
  }

  private static setFavourites(data: any) {
    drupalSettings['user'].favourites = Object.keys(data)
  }

}
